import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, PageHeader, Panel, Spinner, StatusBadge } from "@/components/common";

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function ScheduleSession() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date());
  const [f, setF] = useState({ title: "", subject: user.subject || "", description: "", time: "", duration: "60" });
  const [students, setStudents] = useState([]);
  const [sel, setSel] = useState(loc.state?.studentIds || []);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tutor/students").then((r) => setStudents(r.data));
    api.get("/tutor/sessions", { params: { scope: "upcoming" } }).then((r) => setSessions(r.data));
  }, []);

  const dayItems = useMemo(() => sessions.filter((s) => sameDay(new Date(s.start_at), date)), [sessions, date]);
  const booked = useMemo(() => sessions.map((s) => new Date(s.start_at)), [sessions]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.time) return toast.error("Choose a start time");
    if (!sel.length) return toast.error("Select at least one approved student");
    const [h, m] = f.time.split(":").map(Number);
    const start = new Date(date);
    start.setHours(h, m, 0, 0);
    setBusy(true);
    try {
      await api.post("/tutor/sessions", { title: f.title, subject: f.subject, description: f.description,
        start_at: start.toISOString(), duration: Number(f.duration), student_ids: sel });
      toast.success(`Session scheduled. ${sel.length * 100} credits reserved.`);
      nav("/tutor/upcoming");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Schedule Session" subtitle="Pick a date, choose approved students and create the live meeting. 100 credits are reserved per student." />
      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-5" data-testid="schedule-form">
        <div className="space-y-6 lg:col-span-2">
          <Panel className="p-4">
            <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
              modifiers={{ booked }} modifiersClassNames={{ booked: "font-bold text-[#1F6FFF] underline" }} className="mx-auto" data-testid="schedule-calendar" />
          </Panel>
          <Panel className="p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your sessions on {date.toLocaleDateString()}</p>
            {dayItems.length === 0 ? <p className="mt-3 text-sm text-slate-500">You're free all day.</p> : dayItems.map((s) => (
              <div key={s.id} className="mt-3 flex items-center justify-between rounded-lg bg-[#F4F7FA] px-3 py-2 text-sm">
                <span>{fmtTime(s.start_at)} – {fmtTime(s.end_at)} · {s.title}</span><StatusBadge status={s.status} />
              </div>
            ))}
          </Panel>
        </div>
        <Panel className="space-y-4 p-6 lg:col-span-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Session title</Label><Input required minLength={3} value={f.title} onChange={set("title")} data-testid="schedule-title-input" /></div>
            <div className="space-y-1.5"><Label>Subject</Label><Input required value={f.subject} onChange={set("subject")} data-testid="schedule-subject-input" /></div>
            <div className="space-y-1.5"><Label>Date</Label><Input value={date.toLocaleDateString()} readOnly data-testid="schedule-date-display" /></div>
            <div className="space-y-1.5"><Label>Start time</Label><Input required type="time" value={f.time} onChange={set("time")} data-testid="schedule-time-input" /></div>
            <div className="space-y-1.5"><Label>Duration</Label>
              <Select value={f.duration} onValueChange={(v) => setF({ ...f, duration: v })}>
                <SelectTrigger data-testid="schedule-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["30", "45", "60", "90", "120"].map((d) => <SelectItem key={d} value={d}>{d} minutes</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea value={f.description} onChange={set("description")} data-testid="schedule-description-input" /></div>
          </div>
          <div>
            <Label>Select approved students ({sel.length} selected)</Label>
            <div className="mt-2 max-h-64 space-y-2 overflow-y-auto rounded-xl border border-slate-200 p-2" data-testid="schedule-students-list">
              {students.length === 0 && <p className="p-3 text-sm text-slate-500">No approved students yet.</p>}
              {students.map((s) => (
                <label key={s.id} className={`flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors hover:bg-slate-50 ${!s.eligible ? "opacity-70" : ""}`}>
                  <Checkbox checked={sel.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`schedule-student-${s.id}`} />
                  <Avatar src={s.profile_picture} name={s.name} size="h-8 w-8" />
                  <span className="flex-1 text-sm font-semibold">{s.name}</span>
                  {s.eligible ? <span className="text-xs text-[#2DA638]">Eligible</span> : <span className="text-xs font-semibold text-red-600">Insufficient credits</span>}
                </label>
              ))}
            </div>
          </div>
          <Button disabled={busy} className="h-11 rounded-full bg-[#1F6FFF] px-8 hover:bg-[#1450C9]" data-testid="schedule-submit-button">
            {busy ? <Spinner /> : <CalendarPlus className="h-4 w-4" />} Schedule Meeting
          </Button>
        </Panel>
      </form>
    </div>
  );
}
